import React, { useEffect, useState } from "react";
import { httpGet, httpPost } from "../helpers/httpMethods";
import "./banner.css";

// One managed banner spot. Takes no space when nothing is live (or shows `fallback`, e.g. the old slides).
const device = () => (typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop");
const CAPS = { day1: [1, 864e5], day3: [3, 864e5], week1: [1, 6048e5] };
// Counted per banner version: editing a banner starts the count again.
const capKey = (b) => `tns-banner-${b.id}${b.v ? `-${b.v}` : ""}`;
const seen = (b) => { try { return JSON.parse(localStorage.getItem(capKey(b)) || "[]"); } catch (e) { return []; } };
const capped = (b) => { const c = CAPS[b.frequencyCap]; return c ? seen(b).filter((t) => Date.now() - t < c[1]).length >= c[0] : false; };
const remember = (b) => { try { localStorage.setItem(capKey(b), JSON.stringify([...seen(b).filter((t) => Date.now() - t < 6048e5), Date.now()])); } catch (e) { /* private mode */ } };

// Admin preview: /?banner_preview=<signed link from Banners in the admin>. Shows that banner in its spots for this
// browser tab, whatever its audience, schedule or once-a-day rule. Not counted as a view.
const previewToken = () => {
  try {
    const t = new URLSearchParams(window.location.search).get("banner_preview");
    if (t) sessionStorage.setItem("tns-banner-preview", t);
    return t || sessionStorage.getItem("tns-banner-preview");
  } catch (e) { return null; }
};
let previewOnce = null;
const loadPreview = () => {
  const t = previewToken();
  if (!t) return Promise.resolve(null);
  if (!previewOnce) {
    previewOnce = httpGet(`banners/preview?token=${encodeURIComponent(t)}`).then((r) => {
      const b = r && r.data && r.data.banner;
      if (b) return b;
      try { sessionStorage.removeItem("tns-banner-preview"); } catch (e) { /* ignore */ }
      return { expired: (r && r.message) || "This preview link has expired." };
    });
  }
  return previewOnce;
};
const pick = (list) => {
  const first = list.filter((b) => b.weight >= 100);
  const pool = first.length ? first : list;
  let r = Math.random() * pool.reduce((a, b) => a + b.weight, 0);
  for (const b of pool) { r -= b.weight; if (r <= 0) return b; }
  return pool[0];
};
// Text colour that is always readable on the chosen background.
const readable = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#FFFFFF";
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.45 ? "#14142B" : "#FFFFFF";
};

export default function BannerSlot({ spot, paper, fallback = null, full = false, inline = false }) {
  const [state, setState] = useState({ loaded: false, banner: null });

  useEffect(() => {
    let live = true;
    const t = previewToken();
    if (t) {
      loadPreview().then((b) => {
        if (!live) return;
        const here = b && !b.expired && (b.spots || []).includes(spot) && (!(b.paperIds || []).length || (paper && b.paperIds.includes(paper)));
        setState({ loaded: true, banner: here ? { ...b, preview: true } : null, expired: b && b.expired && spot === "home_top" ? b.expired : null });
      });
      return () => { live = false; };
    }
    httpGet(`banners?spot=${encodeURIComponent(spot)}&device=${device()}${paper ? `&paper=${paper}` : ""}`).then((r) => {
      if (!live) return;
      const list = r && r.data && Array.isArray(r.data.banners) ? r.data.banners.filter((b) => !capped(b)) : [];
      const banner = list.length ? pick(list) : null;
      setState({ loaded: true, banner });
      if (banner) { remember(banner); httpPost(`banners/${banner.id}/event`, { type: "view" }); }
    });
    return () => { live = false; };
  }, [spot, paper]);

  const b = state.banner;
  if (!b && state.expired) return <p className="bn-preview-note bn-preview-bad" role="status">{state.expired}</p>;
  if (!b) return state.loaded ? fallback : fallback; // until a banner arrives, keep the fallback (or nothing)

  const url = b.linkUrl || "";
  const external = /^https?:\/\//i.test(url) && !/^https?:\/\/(www\.)?newstand\.tech/i.test(url);
  const onClick = (e) => {
    if (b.preview) { e.preventDefault(); return; } // preview: don't count, don't leave the page
    const count = httpPost(`banners/${b.id}/event`, { type: "click" });
    if (!url || external) return;
    e.preventDefault();
    Promise.race([count, new Promise((r) => setTimeout(r, 300))]).then(() => { window.location.href = url; });
  };

  const inner = b.kind === "image" ? (
    <picture className="bn-pic">
      {b.mobileImageUrl ? <source media="(max-width: 767px)" srcSet={b.mobileImageUrl} /> : null}
      <img src={b.desktopImageUrl} alt={b.altText || ""} loading="lazy" />
    </picture>
  ) : (
    <div className={`bn-built ${b.layout || "text_left"}`} style={{ background: b.bgColor, color: readable(b.bgColor) }}>
      <div className="bn-text">
        <h2 className="bn-head">{b.headline}</h2>
        {b.message ? <p className="bn-msg">{b.message}</p> : null}
        {b.buttonText ? <span className="bn-btn" style={{ background: b.buttonColor, color: readable(b.buttonColor) }}>{b.buttonText}</span> : null}
      </div>
      {b.imageUrl ? <div className="bn-img"><img src={b.imageUrl} alt="" loading="lazy" /></div> : null}
    </div>
  );

  return (
    <aside className={`bn-wrap${full ? " bn-full" : ""}${inline ? " bn-inline" : ""}${b.preview ? " bn-is-preview" : ""}`} aria-label="Announcement">
      {b.preview ? <p className="bn-preview-note" role="status">Preview · only you can see this. Readers see it according to its audience and schedule.</p> : null}
      {url ? (
        <a className="bn-link" href={url} onClick={onClick} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{inner}</a>
      ) : (
        <div className="bn-link">{inner}</div>
      )}
    </aside>
  );
}
